import { useId, useState } from "react";
import { Banner, FoodsCheckbox, SearchBox, ToggleSwitch, Typography } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useOrganizationSelector } from "src/api/client-api/company/useOrganizationSelector";
import type { Company } from "src/api/client-api/company/organizationSchema";
import { FoodsLoadingIndicator } from "src/components/EntityForm/FoodsFields";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { mergeVisibleSelection } from "./brandAccess";

export type BrandOwner = {
    iri: IRI;
    id: number;
    name: string;
};

export type BrandAccessValues = {
    sharedWithOrganizations: IRI[];
    sharedWithAllSubsidiaries: boolean;
};

type BrandAccessSectionProps = BrandAccessValues & {
    owner: BrandOwner | null;
    onChange: (values: BrandAccessValues) => void;
    errorMessage: string | null;
};

export const BrandAccessSection = (props: BrandAccessSectionProps) => {
    const { owner, sharedWithOrganizations, sharedWithAllSubsidiaries, onChange, errorMessage } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const titleId = useId();

    return (
        <Stack
            gap={theme.spacing(2)}
            sx={{ borderTop: `1px solid ${theme.palette.divider}`, paddingTop: theme.spacing(2.5) }}
        >
            <Typography variant="body-bold" id={titleId}>
                {t("brands.access.title", "Companies with access")}
            </Typography>
            {errorMessage !== null && (
                <Box role="alert">
                    <Banner variant="error">
                        <Stack>
                            <Typography variant="small-semibold" sx={{ color: "inherit" }}>
                                {t("brands.access.removalRefusedTitle", "Access still in use")}
                            </Typography>
                            <Typography variant="small-regular" sx={{ color: "inherit" }}>
                                {errorMessage}
                            </Typography>
                        </Stack>
                    </Banner>
                </Box>
            )}
            {owner === null ?
                <Typography variant="small-regular" sx={{ color: theme.custom.grey[2200] }}>
                    {t("brands.access.ownerRequired", "Select the owner company to choose who can use the brand.")}
                </Typography>
            :   <>
                    <ToggleSwitch
                        checked={sharedWithAllSubsidiaries}
                        onChange={(_event, checked) =>
                            onChange({ sharedWithOrganizations, sharedWithAllSubsidiaries: checked })
                        }
                        label={
                            <Stack>
                                <Typography variant="small-semibold">
                                    {t("brands.access.allSubsidiaries", "All subsidiaries")}
                                </Typography>
                                <Typography variant="small-regular" sx={{ color: theme.custom.grey[2200] }}>
                                    {t("brands.access.allSubsidiariesHint", "Includes subsidiaries added later.")}
                                </Typography>
                            </Stack>
                        }
                    />
                    <SubsidiaryPicker
                        owner={owner}
                        titleId={titleId}
                        sharedWithOrganizations={sharedWithOrganizations}
                        sharedWithAllSubsidiaries={sharedWithAllSubsidiaries}
                        onChange={onChange}
                    />
                </>
            }
        </Stack>
    );
};

type SubsidiaryPickerProps = BrandAccessValues & {
    owner: BrandOwner;
    titleId: string;
    onChange: (values: BrandAccessValues) => void;
};

const SubsidiaryPicker = (props: SubsidiaryPickerProps) => {
    const { owner, titleId, sharedWithOrganizations, sharedWithAllSubsidiaries, onChange } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [search, setSearch] = useState("");

    const { companies, isLoading, error, refetch } = useOrganizationSelector({ hierarchyRootId: owner.id });
    const subsidiaries = companies.filter((organization) => organization["@id"] !== owner.iri);

    if (isLoading) {
        return <FoodsLoadingIndicator label={t("common.loading", "Loading...")} />;
    }

    if (error) {
        return (
            <RemoteErrorAlert
                message={t("une_erreur_est_survenue", "An error occurred")}
                onRetry={refetch}
                status="server-error"
            />
        );
    }

    const hintStyle = { color: theme.custom.grey[2200] };

    if (sharedWithAllSubsidiaries) {
        return (
            <Typography variant="small-regular" sx={hintStyle}>
                {t(
                    "brands.access.allSubsidiariesSummary",
                    "All current subsidiaries of {{owner}} ({{total}}) have access, and so will any subsidiary added later.",
                    { total: subsidiaries.length, owner: owner.name },
                )}
            </Typography>
        );
    }

    if (subsidiaries.length === 0) {
        return (
            <Typography variant="small-regular" sx={hintStyle}>
                {t("brands.access.noSubsidiary", "{{owner}} has no subsidiary yet.", { owner: owner.name })}
            </Typography>
        );
    }

    const normalizedSearch = search.trim().toLowerCase();
    const visible = subsidiaries.filter((organization) => organization.name.toLowerCase().includes(normalizedSearch));
    const selectedCount = subsidiaries.filter((organization) =>
        sharedWithOrganizations.includes(organization["@id"]),
    ).length;

    const changeVisibleSelection = (visibleSelected: Company[]) =>
        onChange({
            sharedWithAllSubsidiaries,
            sharedWithOrganizations: mergeVisibleSelection(
                sharedWithOrganizations,
                visible.map((organization) => organization["@id"]),
                visibleSelected.map((organization) => organization["@id"]),
            ),
        });

    return (
        <Stack gap={theme.spacing(1)}>
            <Box role="search" aria-label={t("brands.access.searchPlaceholder", "Search a company")}>
                <SearchBox
                    value={search}
                    onChange={setSearch}
                    placeholder={t("brands.access.searchPlaceholder", "Search a company")}
                />
            </Box>
            {visible.length === 0 && (
                <Typography variant="small-regular" sx={hintStyle}>
                    {t("brands.access.noSearchResult", "No subsidiary matches your search.")}
                </Typography>
            )}
            <Box
                role="group"
                aria-labelledby={titleId}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.spacing(1),
                    padding: theme.spacing(1.5),
                    maxHeight: theme.spacing(30),
                    overflowY: "auto",
                }}
            >
                <FoodsCheckbox
                    values={visible}
                    selectedItems={visible.filter((organization) =>
                        sharedWithOrganizations.includes(organization["@id"]),
                    )}
                    onChange={changeVisibleSelection}
                    getId={(organization) => organization["@id"]}
                    getName={(organization) => organization.name}
                />
            </Box>
            <Typography variant="small-regular" sx={hintStyle}>
                {t("brands.access.selectionCount", "{{selected}} of {{total}} subsidiaries of {{owner}} selected", {
                    selected: selectedCount,
                    total: subsidiaries.length,
                    owner: owner.name,
                })}
            </Typography>
        </Stack>
    );
};
